import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import lod_ from "lodash";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Icon,
	IconButton,
	Tooltip,
	CircularProgress
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import TextAreaScrollable from "components/Custom/TextAreaScrollable";
import SourceActions from "redux-react/actions/sourceActions";
import { display } from "redux-react/reducers/snackBarReducer";
import { t } from "i18next";

/**
 * EditSummary component
 * Allows editing the summary of a source.
 * @param {Object} props - Component properties
 * @param {Object} props.source - The source containing the summary to edit.
 * @param {Function} props.reload - Function to reload the source after updating.
 * @returns {JSX.Element} The rendered component.
 */
const EditSummary = ({ source, reload = () => {} }) => {
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);
	const [summary, setSummary] = useState(source?.summary || "");
	const [openDialog, setOpenDialog] = useState(false);

	/**
	 * Handle save action
	 * @returns {void}
	 */
	const onSave = () => {
		if (lod_.isNil(source?.code)) {
			return;
		}
		setLoading(true);
		dispatch(
			SourceActions.updateSource(
				source.code,
				{ summary },
				() => {
					dispatch(
						display({
							type: "success",
							message: t("FAIQ.SUCCESS.summaryUpdated")
						})
					);
					setLoading(false);
					setOpenDialog(false);
					reload();
				},
				() => {
					setLoading(false);
					dispatch(
						display({
							type: "error",
							message: t("FAIQ.ERROR.errorWhileUpdateSummary")
						})
					);
				}
			)
		);
	};

	useEffect(() => {
		setSummary(source?.summary || "");
	}, [source]);

	return (
		<MDBox>
			{/* Button */}
			<Tooltip title={t("SETTINGS.edit")} placement="top">
				<IconButton size="small" onClick={() => setOpenDialog(true)}>
					<Icon>edit</Icon>
				</IconButton>
			</Tooltip>
			{/* Dialog */}
			<Dialog
				fullWidth
				open={openDialog}
				PaperProps={{
					sx: {
						height: "95%",
						width: "100%",
						maxWidth: "100%"
					}
				}}
			>
				<DialogTitle>{t("FAIQ.ACTIONS.editSummary")}</DialogTitle>
				<DialogContent
					sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}
				>
					<TextAreaScrollable
						value={summary}
						onChange={e => setSummary(e.target.value)}
						placeholder={t("FAIQ.ACTIONS.editSummaryPlaceholder")}
						disabled={loading}
					/>
				</DialogContent>
				<DialogActions>
					<MDButton
						variant="outlined"
						color="info"
						onClick={() => setOpenDialog(false)}
						disabled={loading}
					>
						{t("SETTINGS.cancel")}
					</MDButton>
					{loading === false && (
						<MDButton variant="contained" color="info" onClick={onSave} disabled={loading}>
							{t("SETTINGS.save")}
						</MDButton>
					)}
					{loading === true && (
						<MDButton variant="contained" color="info" disabled>
							<CircularProgress size={24} color="white" />
							&nbsp;{t("SETTINGS.saving")}
						</MDButton>
					)}
				</DialogActions>
			</Dialog>
		</MDBox>
	);
};

export default EditSummary;
